import { supabase } from '../lib/supabase';
export class FirmwareService {
    /**
     * Get the latest released firmware version
     */
    static async getLatestFirmwareVersion() {
        const { data, error } = await supabase.rpc('get_latest_firmware_version');
        if (error) {
            console.error('Error fetching latest firmware version:', error);
            throw new Error(error.message);
        }
        if (!data || data.length === 0)
            return null;
        return data[0];
    }
    /**
     * Check if a firmware update is available
     */
    static async checkForUpdate(currentVersion) {
        const latest = await this.getLatestFirmwareVersion();
        if (!latest || !currentVersion) {
            return {
                isUpdateAvailable: false,
                currentVersion,
                latestVersion: latest?.version ?? null,
                latestFirmware: latest
            };
        }
        const isNewer = this.isNewerVersion(latest.version, currentVersion);
        return {
            isUpdateAvailable: isNewer,
            currentVersion,
            latestVersion: latest.version,
            latestFirmware: latest
        };
    }
    /**
     * Compare two semver strings. Returns true if candidate is newer than current.
     */
    static isNewerVersion(candidate, current) {
        const candidateParts = candidate.split('.').map(Number);
        const currentParts = current.split('.').map(Number);
        for (let i = 0; i < Math.max(candidateParts.length, currentParts.length); i++) {
            const a = candidateParts[i] || 0;
            const b = currentParts[i] || 0;
            if (a > b)
                return true;
            if (a < b)
                return false;
        }
        return false;
    }
    /**
     * Get a signed download URL for a firmware file
     */
    static async getFirmwareDownloadUrl(filePath) {
        const { data, error } = await supabase.storage
            .from('firmware')
            .createSignedUrl(filePath, 3600);
        if (error) {
            console.error('Error creating signed URL:', error);
            throw new Error(error.message);
        }
        return data.signedUrl;
    }
    /**
     * Get all firmware versions (for web admin management page)
     */
    static async getAllFirmwareVersions() {
        const { data, error } = await supabase
            .from('firmware_versions')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error fetching firmware versions:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    /**
     * Create a new firmware version record (web admin)
     */
    static async createFirmwareVersion(version, description, releaseNotes, filePath, fileSizeBytes, isReleased) {
        const { data, error } = await supabase
            .from('firmware_versions')
            .insert({
            version,
            description,
            release_notes: releaseNotes,
            file_path: filePath,
            file_size_bytes: fileSizeBytes,
            is_released: isReleased
        })
            .select()
            .single();
        if (error) {
            console.error('Error creating firmware version:', error);
            if (error.code === '23505') {
                throw new Error('A firmware version with this version number already exists');
            }
            throw new Error(error.message);
        }
        return data;
    }
    /**
     * Update a firmware version record (web admin)
     */
    static async updateFirmwareVersion(id, updates) {
        const { data, error } = await supabase
            .from('firmware_versions')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) {
            console.error('Error updating firmware version:', error);
            throw new Error(error.message);
        }
        return data;
    }
    /**
     * Delete a firmware version record (web admin)
     */
    static async deleteFirmwareVersion(id) {
        const { error } = await supabase
            .from('firmware_versions')
            .delete()
            .eq('id', id);
        if (error) {
            console.error('Error deleting firmware version:', error);
            throw new Error(error.message);
        }
    }
    /**
     * Upload a .gbl firmware file to storage (web admin)
     */
    static async uploadFirmwareFile(file, path) {
        const { error } = await supabase.storage
            .from('firmware')
            .upload(path, file, {
            cacheControl: '3600',
            upsert: false
        });
        if (error) {
            console.error('Error uploading firmware file:', error);
            throw new Error(error.message);
        }
        return path;
    }
    /**
     * Delete a firmware file from storage (web admin)
     */
    static async deleteFirmwareFile(path) {
        const { error } = await supabase.storage
            .from('firmware')
            .remove([path]);
        if (error) {
            console.error('Error deleting firmware file:', error);
            throw new Error(error.message);
        }
    }
}
